import { ApiAuthenticationFailedException } from '../errors'
import { buildQueryString } from '../utils/buildQueryString'
import { createRestApiClient } from '../utils/createRestApiClient'
import { backpackAccountCategory } from './constants'

const BASE_URL = 'https://api.backpack.exchange'

/** 既定の window (ms) - Backpack 仕様で必須の値 */
const DEFAULT_WINDOW_MS = 5000

/**
 * ED25519 秘密鍵を pkcs8 形式に変換するための DER prefix。
 * `30 2e 02 01 00 30 05 06 03 2b 65 70 04 22 04 20` の固定値で、
 * 32 byte の seed を末尾に連結すると ED25519 用 pkcs8 DER となる。
 */
const ED25519_PKCS8_PREFIX = new Uint8Array([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04,
  0x22, 0x04, 0x20,
])

/**
 * base64 文字列を Uint8Array に変換する。
 * @param value - base64 エンコードされた文字列
 * @returns デコードされたバイト列
 */
const base64ToBytes = (value: string): Uint8Array => {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/**
 * Uint8Array を base64 文字列に変換する。
 * @param bytes - エンコード対象のバイト列
 * @returns base64 文字列
 */
const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * Backpack の署名仕様に従って ED25519 で payload を署名する。
 * @param params.apiSecret - base64 エンコードされた 32 byte の ED25519 seed
 * @param params.payload - 署名対象の文字列
 * @returns base64 エンコードされた署名
 */
const signPayload = async (params: {
  apiSecret: string
  payload: string
}): Promise<string> => {
  const { apiSecret, payload } = params

  const seed = base64ToBytes(apiSecret)
  if (seed.length !== 32) {
    throw new Error('Backpack API secret must be a 32-byte ED25519 seed')
  }

  const pkcs8 = new Uint8Array(ED25519_PKCS8_PREFIX.length + seed.length)
  pkcs8.set(ED25519_PKCS8_PREFIX, 0)
  pkcs8.set(seed, ED25519_PKCS8_PREFIX.length)

  const key = await crypto.subtle.importKey(
    'pkcs8',
    pkcs8,
    { name: 'Ed25519' },
    false,
    ['sign'],
  )
  const signature = await crypto.subtle.sign(
    'Ed25519',
    key,
    new TextEncoder().encode(payload),
  )

  return bytesToBase64(new Uint8Array(signature))
}

/**
 * Backpack 仕様の署名対象 payload を生成する。
 * `instruction=<name>&<sortedQueryString>&timestamp=<ts>&window=<window>` 形式。
 * @param params.instruction - 各 endpoint で定義された instruction 名
 * @param params.params - クエリ or body のパラメータ
 * @param params.timestamp - X-Timestamp ヘッダー値 (ms)
 * @param params.window - X-Window ヘッダー値 (ms)
 * @returns 署名対象文字列
 */
const buildSigningPayload = (params: {
  instruction: string
  params?: Record<string, unknown>
  timestamp: number
  window: number
}): string => {
  const { instruction, params: requestParams, timestamp, window } = params

  const sortedQuery = buildQueryString(requestParams)
  const prefix = `instruction=${instruction}`
  const middle = sortedQuery ? `&${sortedQuery}` : ''
  return `${prefix}${middle}&timestamp=${timestamp}&window=${window}`
}

/**
 * Backpack API 用の REST クライアントを生成する。
 * @param params.apiKey - base64 エンコードされた ED25519 公開鍵
 * @param params.apiSecret - base64 エンコードされた ED25519 秘密鍵 seed
 * @param params.createHeaders - 上書き適用するヘッダー生成関数 (プロキシ認証等)
 * @param params.proxy - プロキシ経由する場合の base URL
 * @returns 認証情報を埋め込んだ REST クライアント
 */
export const createClient = ({
  apiKey,
  apiSecret,
  createHeaders,
  proxy,
}: {
  /** base64 エンコードされた ED25519 公開鍵 */
  apiKey: string
  /** base64 エンコードされた ED25519 秘密鍵 seed */
  apiSecret: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  /** request を proxy する場合の proxy base URL */
  proxy?: string
}) =>
  createRestApiClient({
    buildRequestData: async ({ method, path, query, body }) => {
      const queryString = buildQueryString(query)
      const queryWithQuestionMark = queryString ? `?${queryString}` : ''
      const url = `${BASE_URL}${path}${queryWithQuestionMark}`

      const instruction = resolveInstructionForPath({ method, path })
      const signingParams: Record<string, unknown> | undefined =
        method === 'POST' && body ? body : query

      const timestamp = Date.now()
      const window = DEFAULT_WINDOW_MS
      const payload = buildSigningPayload({
        instruction,
        params: signingParams,
        timestamp,
        window,
      })
      const signature = await signPayload({ apiSecret, payload })

      const proxyHeaders = createHeaders ? await createHeaders() : {}

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey,
        'X-Signature': signature,
        'X-Timestamp': timestamp.toString(),
        'X-Window': window.toString(),
        ...proxyHeaders,
      }

      return {
        url,
        headers,
        body: method === 'POST' && body ? JSON.stringify(body) : undefined,
      }
    },
    getDelayForRetry: ({ statusCode, errorMessage }) => {
      // 401 / 403 は認証失敗
      if (statusCode === 401) {
        throw new ApiAuthenticationFailedException(backpackAccountCategory)
      }
      if (
        statusCode === 403 &&
        (errorMessage.includes('Invalid') ||
          errorMessage.includes('Unauthorized'))
      ) {
        throw new ApiAuthenticationFailedException(backpackAccountCategory)
      }
      // 429 はレートリミット → 10 秒待機
      if (statusCode === 429) {
        return 10_000
      }
      // 5xx は 1 秒待機
      if (statusCode >= 500) {
        return 1000
      }
      return undefined
    },
    proxy,
  })

/**
 * Backpack の各 endpoint に対応する instruction 名を path / method から解決する。
 * 署名対象の payload に prefix する `instruction=<name>` 部分で必要。
 * @throws path / method の組合せが未対応の場合
 */
const resolveInstructionForPath = (params: {
  method: 'GET' | 'POST'
  path: string
}): string => {
  const { method, path } = params

  if (method === 'GET') {
    if (path === '/api/v1/capital') {
      return 'balanceQuery'
    }
    if (path === '/api/v1/capital/collateral') {
      return 'collateralQuery'
    }
    if (path === '/wapi/v1/capital/deposits') {
      return 'depositQueryAll'
    }
    if (path === '/wapi/v1/capital/withdrawals') {
      return 'withdrawalQueryAll'
    }
    if (path === '/wapi/v1/history/fills') {
      return 'fillHistoryQueryAll'
    }
    if (path === '/wapi/v1/history/funding') {
      return 'fundingHistoryQueryAll'
    }
    if (path === '/wapi/v1/history/interest') {
      return 'interestHistoryQueryAll'
    }
    if (path === '/wapi/v1/history/borrowLend') {
      return 'borrowHistoryQueryAll'
    }
    if (path === '/wapi/v1/history/settlement') {
      return 'settlementHistoryQueryAll'
    }
  }

  throw new Error(`Unsupported Backpack endpoint: ${method} ${path}`)
}
